import { errorIsStatus, type GetPaths, useClientPortalAPI } from "@certn/api";
import {
  Avatar,
  ColumnDefinition,
  Container,
  DataTable,
  NeutralTag,
  Pagination,
  SectionMessage,
  SuccessTag,
} from "@certn/cascadia-react";
import { BasicButton, Text } from "@certn/cascadia";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { StatusFilter } from "./StatusFilter";
import { useCheckArgumentsTemplatesInPackages } from "./useCheckArgumentsTemplatesInPackages";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

export interface CheckArgumentsTemplateListRow {
  id: string;
  name: string;
  is_active: boolean;
  is_default?: boolean;
  created: string;
  created_by?: { auth0_full_name?: string | null } | null;
}

export interface CheckArgumentsTemplateListConfig {
  listApiPath: string;
  settingsPathPrefix: string;
  /** Must call `t` with a string literal so i18next-parser extracts the key. */
  pageTitle: (t: TFunction) => string;
  testIdPrefix: string;
  apiPathPrefix: string;
  filterBarTestId: string;
  showCertnForDefaultTemplateWithoutCreator?: boolean;
}

function StatusTag({
  isActive,
  testIdPrefix,
}: {
  isActive: boolean;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();

  if (isActive) {
    return (
      <SuccessTag testID={`${testIdPrefix}-active-tag`} type="subtle">
        {t("Active")}
      </SuccessTag>
    );
  }

  return (
    <NeutralTag testID={`${testIdPrefix}-deactivated-tag`} type="subtle">
      {t("Deactivated")}
    </NeutralTag>
  );
}

export function CheckArgumentsTemplateListView(
  config: CheckArgumentsTemplateListConfig
) {
  const {
    listApiPath,
    settingsPathPrefix,
    pageTitle,
    testIdPrefix,
    apiPathPrefix,
    filterBarTestId,
    showCertnForDefaultTemplateWithoutCreator = false,
  } = config;

  return function ListView() {
    const { t } = useTranslation();
    const title = pageTitle(t);
    const { params, setParam } = useQueryParams();
    const navigate = useNavigate();
    const { data, isLoading, error, mutate } = useClientPortalAPI(
      listApiPath as GetPaths,
      queryParamsToApiParams(params)
    );

    const templateIdsBelongingToActivePackages =
      useCheckArgumentsTemplatesInPackages();

    const listData = data as
      | { results: CheckArgumentsTemplateListRow[]; count: number }
      | undefined;
    const results = listData?.results;
    const activeTemplates = results?.filter(
      (template) => template.is_active === true
    );

    const columns: ColumnDefinition<CheckArgumentsTemplateListRow>[] = [
      {
        key: "name",
        name: t("Name"),
        width: "25%",
        isRowHeader: true,
        allowsSorting: true,
        testID: "name-column-header",
        render({ name }) {
          return (
            <Text style="form/body-secondary" testID={`${testIdPrefix}-name`}>
              {name}
            </Text>
          );
        },
      },
      {
        key: "is_active",
        name: t("Status"),
        width: "25%",
        isRowHeader: true,
        allowsSorting: true,
        testID: "status-column-header",
        render({ is_active }) {
          return <StatusTag isActive={is_active} testIdPrefix={testIdPrefix} />;
        },
      },
      {
        key: "created",
        name: t("Created date"),
        width: "25%",
        isRowHeader: true,
        allowsSorting: true,
        testID: "created-date-column-header",
        render({ created }) {
          return (
            <Text style="form/body-secondary" testID="created-date">
              {formatDate(created, "MMM dd, yyyy")}
            </Text>
          );
        },
      },
      {
        key: "created_by",
        name: t("Created by"),
        width: "25%",
        isRowHeader: true,
        testID: "created-by-column-header",
        render(item) {
          if (item.created_by) {
            return (
              <Avatar
                size="small"
                name={parseName(item.created_by.auth0_full_name || undefined)}
              />
            );
          }
          if (
            showCertnForDefaultTemplateWithoutCreator &&
            item.is_default === true
          ) {
            return <Avatar size="small" name="Certn" />;
          }
        },
      },
      {
        key: "actions",
        isAction: true,
        width: 48,
        render(template) {
          return (
            <ActionsDropdown
              templateIdsBelongingToActivePackages={
                templateIdsBelongingToActivePackages
              }
              numActiveTemplates={activeTemplates?.length || 0}
              mutate={mutate}
              templateId={template.id}
              name={template.name}
              isActive={template.is_active}
              settingsPathPrefix={settingsPathPrefix}
              apiPathPrefix={apiPathPrefix}
            />
          );
        },
      },
    ];

    return (
      <Container layout="full-width">
        <PageTitle
          actions={
            <BasicButton
              testID="new-template-button"
              onPress={() => navigate(`/settings/${settingsPathPrefix}/create`)}
            >
              {t("New template")}
            </BasicButton>
          }
        >
          {title}
        </PageTitle>
        <FilterBar hasSearch testID={filterBarTestId}>
          <StatusFilter />
        </FilterBar>
        {error && !errorIsStatus(error, 404) && (
          <SectionMessage
            type="danger"
            icon="error"
            sectionTitle={t("Unable to retrieve templates")}
          >
            {String(error.message)}
          </SectionMessage>
        )}
        <DataTable
          isLoading={isLoading}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          onRowAction={(id) => {
            navigate(`/settings/${settingsPathPrefix}/${id}`);
          }}
          sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
          columns={columns}
          rows={results ? results : []}
          aria-label={t("{{label}} table", { label: title })}
          testId={`${testIdPrefix}-list-view-data-table`}
        />
        <Pagination
          setPage={(page) => setParam("page", page.toString())}
          setPageCount={(pageCount) =>
            setParam("page_size", pageCount.toString())
          }
          total={listData?.count ?? 0}
          page={params.page ? Number(params.page) : 1}
          pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
        />
      </Container>
    );
  };
}
